import type { RovoInstrumentedError } from '@atlassian/conversation-assistant-utils/httpUtils';

export const PINNED_CONVERSATION_LIMIT_ERROR_CODE =
	'convoai.rest.pinned.conversation.limit.exceeded' as const;

export type PinnedConversationLimitErrorData = {
	maxPinnedConversations?: number;
	pinnedCount?: number;
};

export class ApiError extends Error implements RovoInstrumentedError {
	/** Structured, non-user-facing details supplied by the API. */
	public data: Record<string, unknown> | undefined;

	constructor(
		public message: string,
		public errorAttributes: {
			/**
			 * Error code / message template from the API - e.g CONVERSATION_CHANNEL_NOT_FOUND
			 */
			errorCode?: string;
			/**
			 * HTTP status code
			 */
			statusCode: number;
			rawErrorMessage?: string;
			traceId?: string;
			requestId?: string;
		},
		data?: Record<string, unknown>,
	) {
		super(message);
		this.data = data;
	}
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

type ApiErrorAttributes = ApiError['errorAttributes'];

const getRecord = (value: unknown): Record<string, unknown> | undefined =>
	typeof value === 'object' && value !== null && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: undefined;

const getApiErrorAttributes = (error: unknown): ApiErrorAttributes | undefined => {
	if (isApiError(error)) {
		return error.errorAttributes;
	}

	return getRecord(getRecord(error)?.errorAttributes) as ApiErrorAttributes | undefined;
};

const getStructuredErrorData = (error: unknown): Record<string, unknown> | undefined => {
	const errorRecord = getRecord(error);
	return getRecord(errorRecord?.data) ?? getRecord(getRecord(errorRecord?.errorAttributes)?.data);
};

const getValidCount = (value: unknown, minimum: number): number | undefined =>
	typeof value === 'number' && Number.isInteger(value) && value >= minimum ? value : undefined;

/**
 * Returns validated structured details for an expected pinned-conversation limit rejection.
 * An empty object still identifies the stable error code when an older backend omits `data`.
 */
export const getPinnedConversationLimitErrorData = (
	error: unknown,
): PinnedConversationLimitErrorData | undefined => {
	const errorAttributes = getApiErrorAttributes(error);
	if (errorAttributes?.errorCode !== PINNED_CONVERSATION_LIMIT_ERROR_CODE) {
		return undefined;
	}

	const data = getStructuredErrorData(error);
	if (!data) {
		return {};
	}

	const maxPinnedConversations = getValidCount(data.maxPinnedConversations, 1);
	const pinnedCount = getValidCount(data.pinnedCount, 0);

	return {
		...(maxPinnedConversations !== undefined && { maxPinnedConversations }),
		...(pinnedCount !== undefined && { pinnedCount }),
	};
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapErrorToApiError = (error: any): ApiError => {
	if (isApiError(error)) {
		return error;
	}

	// Fallback since we expect the error to be an instance of ApiError
	return new ApiError(error.message, {
		statusCode: 0,
	});
};
